<template>
  <!-- Modal on first load and error modals -->
  <div class="modal" v-if="openModal === true" :class="{ visible: openModal }">
    <div
      class="frame visible"
    >
      <div class="close" @click="closeModal">
        <i class="fa far fa-times"></i>
      </div>

      <!-- Postcode info when user first land -->
      <div class="postcode-info">
        <div class="banner">
          <!-- <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4796/dist/assets/img/my-backyard-modal-banner.png" /> -->
        </div>

        <!-- ERROR MESSAGE -->
        <div class="content error-message" v-if="isError">
          <div
            class="error-icon"
            style="background-image: url('https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4796/dist/assets/img/error-icon.png')"
          ></div>

          <h4>{{ errorText[modalType] }}</h4>
          <button type="button" name="dismiss" @click="dismissErrorMessage">
            Dismiss
          </button>
        </div>

        <!-- DEFAULT CONTENT -->
        <div class="content" v-else>
          <h2>{{ title }}</h2>
          <h4>{{ subtitle }}</h4>
          <p>{{ petitionCount }} {{ descriptionLine1 }}</p>
          <p class="description-line-2">{{ petitionGoal }} {{ descriptionLine2 }}</p>

          <form @submit.prevent="validateForm">
            <input
              type="text"
              placeholder="ENTER POSTCODE"
              v-model.trim="postcode"
              data-vv-name="postcode"
              v-validate="{ required: true, regex: /^\d{4}$/ }"
              maxlength="4"
            >
            <button type="submit">SEARCH YOUR AREA</button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import firebase from "firebase";

export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    descriptionLine1: {
      type: String,
      required: true,
    },
    descriptionLine2: {
      type: String,
      required: true,
    },
    petitionGoal: {
      type: Number,
    },
    datalayerName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      postcode: null,
      isSaving: false,
      petitionCount: 0,
      errorText: {
        noElectorateError: "Enter your address to find what animals could live near you",
        invalidAddressError: "Your address or postcode can't be shorter than 4 characters.",
        addressNotFoundError: "This address could not be found. Please make sure it is an Australian address.",
      },
    };
  },
  async created() {
    firebase
      .firestore()
      .collection("campaigns")
      .doc(this.datalayerName)
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.data();
          if (!!data) {
            this.petitionCount = data.count;
          }
        },
        (error) => {
          console.error(error);
        }
      );
  },
  computed: {
    ...mapGetters(["openModal", "modalType"]),
    isError() {
      return this.modalType.endsWith("Error");
    },
  },
  methods: {
    closeModal() {
      this.$store.commit("closeModal");

      if (this.modalType === "postcodeInfo") {
        // Log closed intro
        this.$store.dispatch("logSimpleEvent", {
          eventName: "Intro Panel Close Manual",
          eventProperties: { "Close Panel": true },
        });
      }
    },

    dismissErrorMessage() {
      this.postcode = null;
      this.$store.commit("openModal", "postcodeInfo");
    },

    async validateForm() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        if (await this.validate()) {
          await this.selectAddress();
        }

      } catch (err) {
        this.isSaving = false;
      } finally {
        this.isSaving = false;
      }
    },

    async validate() {
      try {
        const result = await this.$validator.validateAll();

        if (result) {
          return result;
        }

        this.$store.commit("openModal", "invalidAddressError");
        return result;
      } catch (error) {
        throw error;
      }
    },

    async selectAddress() {
      // Close potentially opened popup
      this.$store.commit("updateOpenInformationPopup", false);
      this.$store.commit("closeModal");

      try {
        // Update electorate
        const electorates = await this.$store.dispatch(
          "getElectorates",
          this.postcode
        );
        this.$store.commit(
          "updateElectorateName",
          electorates[0].electoraldivisionname
        );

        // Update electorate Grade
        const electorateGrade = await this.$store.dispatch(
          "getElectoratesGrade",
          [electorates[0].electoraldivisionname]
        );
        this.$store.commit(
          "updateElectorateGrade",
          electorateGrade[0].overallgrade
        );

        // Get drawer data
        const [speciesData, otherSpecies] = await Promise.all([
          this.$store.dispatch("getHeroSpecies", {
            electorate: electorates[0].electoraldivisionname,
            ishero: true,
          }),
          this.$store.dispatch("getSpecies", {
            electoraldivisionname: electorates[0].electoraldivisionname,
            isfeatured: true,
          }),
        ]);

        speciesData.speciesCount =
          electorateGrade[0].numberofthreatenedspeciesinelectorate;
        this.$store.commit("updateSpeciesData", speciesData);

        // Update otheer species store
        this.$store.commit("updateOtherSpecies", otherSpecies);

        // Scroll to species details
        document.getElementById("species-details").scrollIntoView({
          behavior: "smooth",
        });

        // Open drawer
        if (
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          )
        ) {
          this.$store.commit("updateOpenDrawer", false);
        } else {
          this.$store.commit("updateOpenDrawer", true);
        }
      } catch (err) {
        console.error(err);
        // Error modal
        this.$store.commit("openModal", "addressNotFoundError");
      }
    },
  },
};
</script>

<style scoped>
/* Style from elcom */
h1, h2, h3 {
  font-family: "WWF",sans-serif;
  position: relative;
  z-index: 1;
}

.modal {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  background-color: rgba(0,0,0, 0.8);
}

.modal.visible {
  display: flex;
}

.modal .frame {
  position: relative;
  display: flex;
  flex-direction: row;
  max-width: 850px;
  background-color: var(--white);
  width: 34%;
  margin-top: 5%;
}

.modal .frame h2,
.modal .frame p,
.modal .frame h4,
.modal .frame i {
  color: var(--black);
  margin: 0;
  text-align: left;
}

.postcode-info {
  width: 100%;
  display: flex;
  min-height: 350px;
}

.postcode-info .banner {
  flex: 0 0 38%;
  background-image: url('https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4796/dist/assets/img/my-backyard-modal-banner.png');
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.postcode-info .banner img {
  width: 100%;
}

.postcode-info .content {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
  padding: 34px 24px 24px;
}

.postcode-info .content h2 {
  text-transform: uppercase;
  font-size: 40px;
  font-weight: 400;
  line-height: 45px;
}

.postcode-info .content h4 {
  font-family: Arial;
  font-size: 0.9em;
  font-weight: 700;
  line-height: 130%;
  text-transform: uppercase;
}

.postcode-info .content p {
  font-family: Arial;
  font-size: 0.9em;
  font-weight: 400;
  line-height: 159%;
}

.postcode-info .content form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.postcode-info .content form input {
  width: 74%;
  border: 1px solid #000000;
  border-radius: 4px;
  margin: 0;
}

.postcode-info .content form input::placeholder {
  font-family: "WWF",sans-serif;
  color: #A0A0A0;
  font-size: 24px;
  font-weight: 400;
  text-transform: uppercase;
}

.postcode-info .content button {
  width: 74%;
  height: 48px;
  padding: 10px 40px;
  background: #F37043;
  font-size: 24px;
  font-weight: 400;
  text-transform: uppercase;
  white-space: nowrap;
}

.postcode-info .content.error-message {
  justify-content: center;
  align-items: center;
}

.postcode-info .content.error-message h4 {
  text-align: center;
}

.modal .frame .close {
  position: absolute;
  top: calc(0.5 * var(--space));
  right: calc(1 * var(--space));
  cursor: pointer;
}

.modal .frame .close i {
  color: #000000;
}

.modal .frame.visible {
  display: flex;
}

.error-icon {
  width: 48px;
  height: 48px;
  background-size: cover;
  background-repeat: no-repeat;
}

button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 70px;
  font-family: var(--font-secondary);
  font-size: 1.5em;
  color: #fff;
  border-radius: var(--border-radius);
  background-color: var(--wwfRegenerateAustralia);
  cursor: pointer;
  text-transform: uppercase;
  padding: 24px;
  margin: 0;
}

.postcode-info .content p.description-line-2 {
  margin-top: -10px;
}

@media screen and (max-width: 1536px) {
  .modal .frame {
    width: 38%;
  }
}

@media screen and (max-width: 1440px) {
  .modal .frame {
    width: 41%;
  }

  .postcode-info .content {
    gap: 7px;
  }
}

@media screen and (max-width: 1280px) {
  .modal .frame {
    width: 45%;
  }
}

@media screen and (max-width: 1250px) {
  .modal .frame {
    width: 50%;
  }
}

@media screen and (max-width: 1024px) {
  .modal .frame {
    width: 55%;
  }
}

@media screen and (max-width: 912px) {
  .modal .frame {
    width: 63%;
  }
}

@media screen and (max-width: 820px) {
  .modal .frame {
    width: 70%;
  }
}

@media screen and (max-width: 801px) {
  .modal .frame {
    width: 75%;
  }
}

@media screen and (max-width: 700px) {
  .modal .frame {
    width: 86%;
  }

  .modal .frame .postcode-info {
    flex-direction: column;
  }

  .modal .frame .postcode-info .banner {
    flex: none;
    height: 200px;
    overflow: hidden;
  }

  .modal .frame .postcode-info .banner img {
    width: 100%;
    margin-top: -26%;
  }

  .modal .frame .postcode-info .content {
    padding: 24px;
  }

  .modal .frame .postcode-info .content form input,
  .modal .frame .postcode-info .content form button {
    width: 100%;
  }
}

@media only screen and (max-device-width: 667px) and (orientation: landscape) {
  .modal .frame {
    width: 75%;
  }

  .modal .frame .postcode-info {
    flex-direction: row;
  }

  .modal .frame .postcode-info .banner {
    flex: 0 0 38%;
    height: auto;
    overflow: none;
  }
}

@media only screen and (max-width: 64em) {
  .postcode-info .content h2 {
    font-size: 34px;
    font-weight: 700;
  }
}
@media only screen and (max-width: 40em) {
}

@media only screen and (max-width: 40em) {
}
/* END */
</style>



// WEBPACK FOOTER //
// ThreatenedSpeciesModal.vue?44139b86